/*
  Seats on the team pages, and what the server says when it refuses one.

  With sign-up first on, a workspace with no plan has two seats: the owner's
  and one to give. An invite that is still out holds that one, and the server
  refuses a second, so the pages count it, or they offer a button that
  cannot work. Every other workspace is counted the way it always was on
  these pages: members against the plan's seats.
*/

/** The owner and the one person they may invite. FREE_TEAM_MEMBER_LIMIT in server/src/config/teamLimits.ts. */
export const FREE_WORKSPACE_SEATS = 2

/**
 * What a workspace with no plan is told once its one seat is taken or
 * offered. The server sends the same words when a press is refused
 * (FREE_TEAM_SEAT_TAKEN_MESSAGE in server/src/services/teamService.ts).
 */
export const FREE_SEAT_TAKEN_SENTENCE = 'A workspace without a plan can invite one person, and that seat is taken or already offered. A plan adds more seats.'

/** The tier of a team with no plan, as the server names it (useTierStatus). */
const NO_PLAN_TIER = 'free'

/**
 * Is this a workspace living by the one-seat rule?
 *
 * Four things, all needed. Sign-up first is on: with it off these pages are
 * as they were. The workspace is not paying. The server told it two seats:
 * a workspace whose plan could not be read just now also reads as not
 * paying, but it is told its plan's own seats and is drawn as that plan.
 *
 * And the server said its tier is the one with no plan. Two seats alone is
 * not the mark of that: the entry plan has two seats as well, and a team on
 * it whose payment is being retried reads as not paying. Its tier is still
 * its plan's, so it is never told it is "a workspace without a plan". The
 * tier has to be the server's answer (`tierKnown`): until it has been read
 * it is only assumed to be free, and nobody is told they have no plan on an
 * assumption.
 */
export function isFreeWorkspace({ isPro, maxMembers, signupFirst, tier, tierKnown }: {
  isPro: boolean
  maxMembers: number
  signupFirst: boolean
  /** The team's tier, from useTierStatus. */
  tier: string
  /** Whether that tier is the server's answer (useTierStatus's isEntitlementsKnown), not the fallback. */
  tierKnown: boolean
}): boolean {
  return signupFirst && !isPro && maxMembers === FREE_WORKSPACE_SEATS && tierKnown && tier === NO_PLAN_TIER
}

/** How many more people a workspace can invite right now. Infinity when its plan has no limit. */
export function openSeats({ maxMembers, memberCount, pendingInviteCount, freeWorkspace }: {
  maxMembers: number
  memberCount: number
  pendingInviteCount: number
  freeWorkspace: boolean
}): number {
  if (maxMembers === -1) return Infinity
  const held = freeWorkspace ? pendingInviteCount : 0
  return Math.max(0, maxMembers - memberCount - held)
}

/** An invite that is still out: nobody has used it and it has not run out. */
export function isPendingInvite(invite: { used_at?: string | null; expires_at: string }, now: Date = new Date()): boolean {
  return !invite.used_at && new Date(invite.expires_at) > now
}

/*
  The code beside a refusal of one of the team gates (TEAM_GATE_CODES in
  server/src/services/teamService.ts): making an invite, reading one, joining.
*/
export const TEAM_GATE_CODES = {
  /** The team has no plan, and the one invite is not on offer. */
  PLAN_REQUIRED: 'TEAM_PLAN_REQUIRED',
  /** The team has a plan and its seats are full. */
  SEAT_LIMIT: 'TEAM_SEAT_LIMIT',
  /** The team has no plan and its one seat is taken or offered. */
  FREE_SEAT_TAKEN: 'FREE_TEAM_SEAT_TAKEN',
} as const

export type TeamGateCode = (typeof TEAM_GATE_CODES)[keyof typeof TEAM_GATE_CODES]

const KNOWN_CODES: readonly string[] = Object.values(TEAM_GATE_CODES)

/*
  The sentences these refusals came with before they carried a code. Read
  only when a body has no code at all: a server that has not been deployed
  yet, or an answer written by hand somewhere that forgot it.
*/
const SENTENCES_WITHOUT_A_CODE: ReadonlyArray<[string, TeamGateCode]> = [
  ['Team has reached maximum size', TEAM_GATE_CODES.SEAT_LIMIT],
  ['Team invitations require an active subscription', TEAM_GATE_CODES.PLAN_REQUIRED],
  ['A workspace without a plan can invite one person', TEAM_GATE_CODES.FREE_SEAT_TAKEN],
]

/**
 * Which team gate refused, and the sentence the server sent with it.
 *
 * The code decides. The sentence is handed on exactly as it came: the same
 * code arrives with different words from making an invite and from joining,
 * so nothing here swaps it for a fixed one. `code` is null when the failure
 * is not a refusal of a team gate.
 */
export function teamRefusal(error: unknown): { code: TeamGateCode | null; sentence: string | null } {
  const data = (error as { response?: { data?: { code?: unknown; error?: unknown } } } | null | undefined)?.response?.data
  const sentence = typeof data?.error === 'string' && data.error.trim() !== '' ? data.error : null

  if (typeof data?.code === 'string') {
    return { code: KNOWN_CODES.includes(data.code) ? (data.code as TeamGateCode) : null, sentence }
  }

  const match = sentence ? SENTENCES_WITHOUT_A_CODE.find(([words]) => sentence.includes(words)) : undefined
  return { code: match ? match[1] : null, sentence }
}
